import * as React from 'react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useDeal } from '@/contexts/deal-context';
import { HubProvider } from '@/contexts/hub-context';
import { useAttachmentsToken } from '@/hooks/use-attachments-token';
import { useClerkUserIdByEmail } from '@/hooks/use-clerk-user';
import { useInboxMessageParser } from '@/hooks/use-inbox-message-parser';
import { useMessageParser } from '@/hooks/use-message-parser';
import { useMessages } from '@/hooks/use-messages';
import { useNylasGrants } from '@/hooks/use-nylas-grants';
import { useNylasThreads } from '@/hooks/use-nylas-threads';
import { useThreads } from '@/hooks/use-threads';
import { dateToUnixTimestamp } from '@/lib/date';
import { fileToAttachment, stringArrayToEmailName } from '@/lib/email';
import type { ThreadListParams } from '@/types/thread';
import type { MessageContent } from './types/communications';
import { CenterPane } from './panes/center-pane';
import { LeftPane } from './panes/left-pane';
import { RightPane } from './panes/right-pane';

export function CommunicationsHub() {
  const { dealId } = useDeal();

  // Refresh attachment token every 2 minutes while Communication Hub is open
  useAttachmentsToken();

  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [selectedInbox, setSelectedInbox] = React.useState<string>('deal');
  const [selectedFolderId, setSelectedFolderId] = React.useState<string>();
  const [selectedMessageId, setSelectedMessageId] = React.useState<string>();
  const [latestMessageDate, setLatestMessageDate] = useState<number>(0);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [dateRangeFilter, setDateRangeFilter] = React.useState<DateRange | undefined>();
  const { messages: rawMessages, sendMessage, isLoading: isMessagesLoading } = useMessages(dealId ?? '');
  const { threads: rawThreads } = useThreads(dealId ?? '');
  // Get grantIds for different inboxes
  const { grantId: dealGrantId, fetchLatestGrant } = useNylasGrants();
  const { grantId: verifyGrantId } = useNylasGrants('verify@ftld.com');
  const { data } = useClerkUserIdByEmail('verify@ftld.com');
  const verifyUserId = data?.userId;
  const { grantId: userGrantId, grantEmail: userGrantEmail } = useNylasGrants();

  // Get the active grantId based on selected inbox
  const activeGrantId = React.useMemo(() => {
    switch (selectedInbox) {
      case 'deal': {
        return dealGrantId;
      }
      case 'verify@ftld.com': {
        return verifyGrantId;
      }
      case userGrantEmail: {
        return userGrantId;
      }
      default: {
        // eslint-disable-next-line unicorn/no-useless-undefined
        return undefined;
      }
    }
  }, [selectedInbox, dealGrantId, verifyGrantId, userGrantId, userGrantEmail]);

  // Date range derived values
  const hasActiveDateFilter = !!(dateRangeFilter?.from && dateRangeFilter?.to);
  const clearDateRangeFilter = React.useCallback(() => setDateRangeFilter(undefined), []);

  const [nylasParams, setNylasParams] = React.useState<ThreadListParams>({
    grantId: '',
    limit: 15,
    searchQueryNative: searchQuery,
  });

  React.useEffect(() => {
    if (selectedInbox !== 'deal' && activeGrantId) {
      setNylasParams({
        grantId: activeGrantId,
        limit: 15,
        in: selectedFolderId ? [selectedFolderId] : undefined,
        searchQueryNative: searchQuery,
        latestMessageAfter: dateRangeFilter?.from ? dateToUnixTimestamp(dateRangeFilter.from) : undefined,
        latestMessageBefore: dateRangeFilter?.to ? dateToUnixTimestamp(dateRangeFilter.to) : undefined,
      });
    }
  }, [selectedInbox, activeGrantId, selectedFolderId, searchQuery, dateRangeFilter]);

  const {
    threads: nylasThreads,
    isLoading: isNylasLoading,
    isFetching: isNylasFetching,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage,
    isFetchingNextPage,
    currentPage,
  } = useNylasThreads(nylasParams);

  // Reset latest message date when inbox or folder changes
  React.useEffect(() => {
    setLatestMessageDate(0);
  }, [selectedInbox, selectedFolderId]);

  // Check for new messages
  React.useEffect(() => {
    // Only check for new threads if:
    // 1. We're not in the 'deal' inbox
    // 2. We have threads
    // 3. We're not in the initial loading state
    // 4. We're on the first page
    if (
      selectedInbox !== 'deal'
      && nylasThreads.length > 0
      && !isNylasLoading
      && currentPage === 1
    ) {
      const mostRecentDate = Math.max(
        ...nylasThreads.map((thread) => thread.latestMessageReceivedDate ?? 0),
      );

      if (latestMessageDate > 0 && mostRecentDate > latestMessageDate) {
        toast.info('New message received');
      }

      setLatestMessageDate(mostRecentDate);
    }
  }, [nylasThreads, selectedInbox, isNylasLoading, currentPage, latestMessageDate]);

  // Parse messages using both parsers at the top level
  const parsedDealMessages = useMessageParser(rawMessages, rawThreads);
  const parsedInboxMessages = useInboxMessageParser(nylasThreads, activeGrantId);

  // Select which messages to use based on selectedInbox
  const messages = React.useMemo(() => {
    return selectedInbox === 'deal' ? parsedDealMessages : parsedInboxMessages;
  }, [selectedInbox, parsedDealMessages, parsedInboxMessages]);

  // Distinguish between initial loading and background refetching
  const isLoading = selectedInbox === 'deal' ? isMessagesLoading : isNylasLoading;
  const isRefreshing = selectedInbox === 'deal' ? false : isNylasFetching && !isNylasLoading;

  const handleNextPage = React.useCallback(() => {
    setSelectedMessageId(undefined);
    nextPage();
  }, [nextPage, setSelectedMessageId]);

  const handlePrevPage = React.useCallback(() => {
    setSelectedMessageId(undefined);
    prevPage();
  }, [prevPage, setSelectedMessageId]);

  /**
   * Gets the grant ID to use for sending a message.
   * @param content - The message content to check for grant IDs
   * @returns The grant ID to use for sending the message
   */
  async function getSendGrant(content: MessageContent) {
    const { threadGrantId, sendAsGrantId } = content;

    // If we're not replying to a thread, use the sendAsGrantId
    if (!threadGrantId) {
      return sendAsGrantId;
    }

    // If threadGrantId matches our active grants, use it directly
    if (threadGrantId === activeGrantId || threadGrantId === verifyGrantId) {
      return threadGrantId;
    }

    // For old/reissued grants, get the latest valid grantId
    try {
      const latestGrantId = await fetchLatestGrant(threadGrantId);

      if (!latestGrantId) {
        toast.error('No valid grant ID found');
        throw new Error('No valid grant ID found');
      }

      return latestGrantId;
    } catch (error) {
      toast.error('Error getting grant ID');
      throw error;
    }
  }

  const handleSend = async (content: MessageContent) => {
    if (!dealId) {
      console.error('dealId is undefined');
      return;
    }

    if (content.isEmail) {
      const sendGrantId = await getSendGrant(content);
      if (!sendGrantId) {
        console.error('grantId is undefined for email message');
        return;
      }
      if (!content.to) {
        toast.error('Recipients are required for email messages');
        console.error('recipients are required for email message');
        return;
      }

      // Convert File[] to CreateAttachmentRequest[] if attachments exist
      const attachments = content.attachments && content.attachments.length > 0
        ? await Promise.all(content.attachments.map(async (file) => fileToAttachment(file)))
        : undefined;

      await sendMessage({
        deal_id: dealId,
        type: 'email',
        grantId: sendGrantId,
        reply_to_message_id: content.replyToMessageId,
        content: { body: content.html },
        send_at: content.sendAt,
        // Use draft mode if message is scheduled to be sent later
        use_draft: !!content.sendAt,
        to: stringArrayToEmailName(content.to || []),
        cc: content.cc ? stringArrayToEmailName(content.cc) : [],
        bcc: content.bcc ? stringArrayToEmailName(content.bcc) : [],
        subject: content.subject || '',
        template_name: content.templateName,
        attachments,
      });

      // Notify user if message is scheduled to be sent later
      if (content.sendAt) {
        toast.success('Message scheduled to be sent later');
      }
    } else {
      // Send internal messages directly
      await sendMessage({
        deal_id: dealId,
        type: 'internal',
        content: { body: content.html },
        template_name: content.templateName,
      });
    }
  };

  const handleUploadFiles = async (files: File[]) => {
    // In a real app, you would upload these files to your server
    // and get back URLs/IDs for them
    console.log('Files to upload:', files);
  };

  return (
    <HubProvider
      selectedInbox={selectedInbox}
      setSelectedInbox={setSelectedInbox}
      myGrantId={userGrantId}
      myGrantEmail={userGrantEmail}
      verifyGrantId={verifyGrantId}
      verifyUserId={verifyUserId}
      dealThreads={rawThreads || []}
      selectedFolderId={selectedFolderId}
      setSelectedFolderId={setSelectedFolderId}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      dateRangeFilter={dateRangeFilter}
      setDateRangeFilter={setDateRangeFilter}
      clearDateRangeFilter={clearDateRangeFilter}
      hasActiveDateFilter={hasActiveDateFilter}
    >
      <ResizablePanelGroup
        direction="horizontal"
        className="h-full items-stretch"
      >
        <ResizablePanel
          defaultSize={20}
          collapsible
          minSize={25}
          maxSize={35}
          onCollapse={() => setIsCollapsed(true)}
          onExpand={() => setIsCollapsed(false)}
        >
          <LeftPane isCollapsed={isCollapsed} />
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={45} minSize={30}>
          <CenterPane
            communications={messages}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            isFetchingNextPage={isFetchingNextPage}
            onMessageSelect={setSelectedMessageId}
            onSend={handleSend}
            onUploadFiles={handleUploadFiles}
            selectedMessageId={selectedMessageId}
            onNextPage={selectedInbox === 'deal' ? undefined : handleNextPage}
            onPrevPage={selectedInbox === 'deal' ? undefined : handlePrevPage}
            hasNextPage={selectedInbox === 'deal' ? false : hasNextPage}
            hasPrevPage={selectedInbox === 'deal' ? false : hasPrevPage}
          />
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={35} minSize={30}>
          <RightPane
            communication={messages.find((m) => m.id === selectedMessageId) ?? null}
            messageId={selectedMessageId}
            activeGrantId={activeGrantId}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </HubProvider>
  );
}
